<template>
    <DsModal
        class="dd-test-modal"
        :is-open="isOpen"
        :size="dialogSize"
        @close="closeModal"
    >
        <template v-if="rulesFound" #heading>
            <DsModalHeader>
                <template #leading>
                    <DsIconButton name="x" @click="closeModal" />
                </template>
                <template #title>
                    <DsModalHeaderTitle>
                        <template #title>
                            {{ $t('funnel.edit.decision.diamond.button.test.text') }}
                        </template>
                    </DsModalHeaderTitle>
                </template>
                <template #trailing>
                    <DsFilledButton
                        :disabled="loading"
                        class="evaluate-form-button"
                        @click="evaluateForm"
                    >
                        {{ $t('funnel.edit.decision.test.label.evaluate') }}
                    </DsFilledButton>
                </template>
            </DsModalHeader>
        </template>

        <div v-if="!rulesFound" class="no-rules-wrapper">
            <div class="no-rules-content">
                <span class="no-rules-found-title">
                    {{ $t('funnel.edit.decision.diamond.no.rules.found') }}
                </span>
                <DsInlineAlert leading-icon type="subtle">
                    <div class="overview-inline-alert">
                        {{ $t('funnel.edit.decision.diamond.no.rules.found.overview.part1') }}
                        <span class="overview-part2">
                            {{ $t('funnel.edit.decision.diamond.no.rules.found.overview.part2') }}
                        </span>
                        {{ $t('funnel.edit.decision.diamond.no.rules.found.overview.part3') }}
                    </div>
                </DsInlineAlert>

                <div class="no-rules-footer">
                    <DsTextButton
                        class="no-rules-text-button"
                        @click="closeModal"
                    >
                        {{ $t('funnel.edit.decision.diamond.got.it') }}
                    </DsTextButton>
                </div>
            </div>
        </div>

        <div>
            <section v-for="(ruleField, index) in ruleFields" :key="ruleField.field">
                <DecisionDiamondElement
                    data-qa="decision-diamond-context-element"
                    :index="index"
                    :rule-field="ruleField"
                    :dd-keys="ddKeys"
                    @update-value="handleUpdateValue"
                />
            </section>
            <section v-if="showResultEvaluation">
                <div v-if="showDisplayError">
                    <span>
                        {{ $t('funnel.edit.decision.flows.started') }}
                    </span>

                    <ul v-for="(flowLabel, index) in flowLabelArray" :key="`flowLabel-${index}`" class="show-flow-label">
                        <li> {{ flowLabel }} </li>
                    </ul>
                </div>

                <ul v-if="!showDisplayError" class="show-none-error">
                    {{ $t('funnel.edit.decision.diamond.overview.error.general.message') }}
                    <li>{{ $t('funnel.edit.decision.diamond.overview.error.detail.campaign.message') }}</li>
                    <li>
                        {{ sequenceMessageError }} <DsLink :href="supportLink" external>
                            {{ $t('funnel.edit.decision.diamond.overview.link.label') }}
                        </DsLink>
                    </li>
                </ul>
            </section>
        </div>
    </DsModal>
</template>

<script>
import { utils } from '../utils/legacy-utils';
import DecisionDiamondElement from './DecisionDiamondElement.vue';
import { decisionTest, decisionEvaluate } from './decision.api';

export default {
    components: {
        DecisionDiamondElement,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        decisionIds: {
            type: Array,
            required: true,
        },
        sourceId: {
            type: Number,
            required: true,
        },
        funnelId: {
            type: Number,
            required: true,
        },
        flowIdTitle: {
            type: Object,
            required: true,
        },
        ddKeys: {
            type: Object,
            required: true,
        },
    },
    emits: ['close-dd-test-modal'],
    data() {
        return {
            ruleFields: [],
            inputValues: new Map(),
            showResultEvaluation: false,
            flowLabelArray: [],
            loading: false,
            rulesFound: true,
            dialogSize: 'med',
        };
    },
    computed: {
        showDisplayError() {
            return this.flowLabelArray.length > 0;
        },
        sequenceMessageError() {
            return this.$t('funnel.edit.decision.diamond.overview.error.detail.sequence.message');
        },
        supportLink() {
            return this.$t('funnel.edit.decision.diamond.overview.link.support');
        },
    },
    async mounted() {
        try {
            const payload = {
                decisionIds: this.decisionIds.join(','),
                ...this.ddKeys,
            };
            const { ruleFields } = await decisionTest(payload);

            this.ruleFields = ruleFields || [];

            if (this.ruleFields.length === 0) {
                this.rulesFound = false;
                this.dialogSize = 'xs';
            }
        } catch (error) {
            this.ruleFields = [];
        }
    },
    methods: {
        handleUpdateValue(payload) {
            if (Array.isArray(payload.value)) {
                this.inputValues.set(payload.ruleFieldId, payload.value);
            } else {
                this.inputValues.set(payload.ruleFieldId, [payload.value]);
            }
            this.showResultEvaluation = false;
        },
        getValueForRuleField(ruleFieldId) {
            return this.inputValues.get(ruleFieldId);
        },
        async evaluateForm() {
            try {
                this.flowLabelArray = [];
                this.loading = true;
                const formData = new FormData();

                this.ruleFields.forEach((ruleField) => {
                    const key = `${ruleField.subject}_${ruleField.category}_${ruleField.field}`;
                    const value = this.getValueForRuleField(key);

                    if (value) {
                        value.forEach((singleValue) => formData.append(key, singleValue));
                    }
                });

                formData.append('funnelId', this.funnelId);
                formData.append('jGraphId', this.sourceId);
                formData.append('decisionIds', this.decisionIds.join(','));
                formData.append('secondaryKey', this.ddKeys.secondaryKey);
                formData.append('secondaryKeyId', this.ddKeys.secondaryKeyId);

                const { flowsToStart } = await decisionEvaluate(formData);

                if (flowsToStart && flowsToStart.length > 0) {
                    const parsedFlowsToStart = flowsToStart.map((flowId) => utils.removeLongSuffix(flowId));

                    Object.entries(this.flowIdTitle).forEach(([key, value]) => {
                        if (parsedFlowsToStart.includes(parseInt(key, 10))) {
                            this.flowLabelArray.push(value);
                        }
                    });
                }
            } catch (error) {
                this.flowLabelArray = [];
                // eslint-disable-next-line no-console
                console.error({ error });
            } finally {
                this.showResultEvaluation = true;
                this.loading = false;
            }
        },
        closeModal() {
            this.$emit('close-dd-test-modal');
        },
    },
};
</script>

<style scoped lang="scss">
.button-group-action-wrapped {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-top: 20px;
    gap: 5px;
}

.show-none-error {
    color: $color-red-900;

    li {
        margin-top: $spacing-100;
        line-height: $spacing-300;
        list-style: square;
        margin-left: $spacing-250;
    }
}

.show-flow-label {
    color: $color-ink-900;
    li {
        margin-top: $spacing-100;
        line-height: $spacing-300;
        list-style: square;
        margin-left: $spacing-250;
    }
}

.no-rules-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.no-rules-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.no-rules-found-title {
    align-self: flex-start;
    font-size: $spacing-250;
    line-height: $spacing-300;
    font-weight: 400;
    margin-bottom: $spacing-250;
}

.no-rules-footer {
    display: flex;
    justify-content: flex-end;
    width: 100%;
}

.no-rules-text-button {
    width: px-to-rem(80);
    height: $spacing-500;
    font-weight: 500;
    font-size: px-to-rem(14);
    line-height: $spacing-200;
    margin-top: $spacing-250;
    padding: $spacing-150 $spacing-200 $spacing-150 $spacing-200;
}

.overview-inline-alert {
    width: 100%;
}

.overview-part2 {
    font-weight: bold;
    display: inline;
}

</style>
